import { FC, useState } from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import styles from './newComparisonModal.module.scss'
import { ComparisonFilters, QuickCompareOption } from '../ComparisonFilters'
import { lastCompleteMonths, Range, shiftBack } from '../periods'

// What the modal hands back: the two windows as ISO dates, plus the shortcut
// they came from when one was used, so the report can highlight it later.
export interface NewComparisonPayload {
  comparison: { from: string; to: string }
  primary: { from: string; to: string }
  quickCompare: string | null
}

interface NewComparisonModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (payload: NewComparisonPayload) => void
}

const COPY = {
  back: 'Back',
  close: 'Close',
  submit: 'Add New Comparison',
  subtitle:
    'Which period are you interested in? Select a quick period or set custom dates.',
  title: 'New Comparison',
}

const RANGE_FORMAT = 'MMM D, YYYY'

// Opens on the last complete calendar month, matching the shortcut it starts
// with. It used to be the mock's own July 2026, which would have been wrong
// from August onwards.
const DEFAULT_QUICK_COMPARE = 'Previous Month'
const DEFAULT_QUICK_COMPARE_MONTHS = 1

const formatRange = ({ end, start }: Range) =>
  `${start.format(RANGE_FORMAT)} – ${end.format(RANGE_FORMAT)}`

// Calendar days, not instants: the API reads these as UTC days, and an ISO
// timestamp from a picker east of UTC floors to the previous day.
const API_DATE_FORMAT = 'YYYY-MM-DD'

const toWindow = ({ end, start }: Range) => ({
  from: start.format(API_DATE_FORMAT),
  to: end.format(API_DATE_FORMAT),
})

export const NewComparisonModal: FC<NewComparisonModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [primary, setPrimary] = useState<Range>(() =>
    lastCompleteMonths(DEFAULT_QUICK_COMPARE_MONTHS)
  )
  const [comparison, setComparison] = useState<Range>(() =>
    shiftBack(
      lastCompleteMonths(DEFAULT_QUICK_COMPARE_MONTHS),
      DEFAULT_QUICK_COMPARE_MONTHS
    )
  )
  // Which shortcut the comparison came from, and how far back it reaches. Both
  // are needed: the label highlights the pill, the offset re-applies it when
  // the primary period moves.
  const [quickCompare, setQuickCompare] = useState<QuickCompareOption | null>({
    label: DEFAULT_QUICK_COMPARE,
    months: DEFAULT_QUICK_COMPARE_MONTHS,
  })

  if (!isOpen) {
    return null
  }

  const handlePrimaryApply = (start: moment.Moment, end: moment.Moment) => {
    const next = { end, start }
    setPrimary(next)

    // A shortcut is relative to the primary period, so moving that period moves
    // the comparison with it rather than leaving a stale pairing behind.
    if (quickCompare) {
      setComparison(shiftBack(next, quickCompare.months))
    }
  }

  const handleCompareApply = (start: moment.Moment, end: moment.Moment) => {
    setComparison({ end, start })
    // A hand-picked range is no longer one of the shortcuts.
    setQuickCompare(null)
  }

  const handleQuickCompare = (option: QuickCompareOption) => {
    setQuickCompare(option)
    setComparison(shiftBack(primary, option.months))
  }

  // Portalled to the body: the page sits inside a `zoom: 0.9` container, and a
  // fixed overlay rendered inside it does not cover the viewport.
  return createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div>
          <div className={styles.header}>
            <div className={styles.headerText}>
              <h3 className={styles.title}>{COPY.title}</h3>
              <p className={styles.subtitle}>{COPY.subtitle}</p>
            </div>

            <button
              aria-label={COPY.close}
              className={styles.close}
              onClick={onClose}
              type="button"
            >
              <FontAwesomeIcon className={styles.closeIcon} icon={faTimes} />
            </button>
          </div>

          <div className={styles.body}>
            <ComparisonFilters
              activeQuickCompare={quickCompare?.label}
              compareWith={formatRange(comparison)}
              layout="stacked"
              onCompareApply={handleCompareApply}
              onPrimaryApply={handlePrimaryApply}
              onQuickCompare={handleQuickCompare}
              primaryRange={formatRange(primary)}
            />
          </div>
        </div>

        <div className={styles.footer}>
          <span className={styles.summary}>
            {formatRange(primary)} — {formatRange(comparison)}
          </span>

          <div className={styles.actions}>
            <button
              className={styles.backButton}
              onClick={onClose}
              type="button"
            >
              {COPY.back}
            </button>

            <button
              className={styles.submitButton}
              onClick={() =>
                onSubmit({
                  comparison: toWindow(comparison),
                  primary: toWindow(primary),
                  quickCompare: quickCompare?.label ?? null,
                })
              }
              type="button"
            >
              {COPY.submit}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
